import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors, spacing } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import Benefit from 'containers/shared/benefit/benefit';
import noFavoritesIcon from 'assets/icons/no-favorites.svg';
import { EmptyDataHot } from 'components/EmptyDataHot';
import Loader from 'components/common/loader';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
import { useGetUserFavoritesMutation } from 'redux/api/users.api';

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${spacing.md}px 0`,
});

const LoaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: `${spacing.xxxl}px`,
  flex: 1,
});

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: `${spacing.md}px`,
  paddingTop: `${spacing.md}px`,
  '& .benefit-wrapper': {
    flex: '0 0 auto',
    border: `1px solid ${colors.system.border.default}`,
    width: 'calc(50% - 6px)',
    [theme.breakpoints.up('sm')]: {
      width: 'calc(33.333% - 8px)',
    },
    [theme.breakpoints.up('lg')]: {
      width: 'calc(25% - 9px)',
    },
  },
}));

export const FavoritesSection = ({ testID }) => {
  const [favoritesMutation, { isLoading }] = useGetUserFavoritesMutation();
  const [favorites, setFavorites] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const { translate } = useTranslations();
  const userData = useSelector((store) => store.config.UserData);
  const previousFavoritesCountRef = useRef(null);

  const fetchFavorites = async () => {
    try {
      const response = await favoritesMutation().unwrap();
      setFavorites(response);
      return response;
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [favoritesMutation]);

  useEffect(() => {
    if (userData?.favoritesArr && previousFavoritesCountRef.current === null) {
      previousFavoritesCountRef.current = userData.favoritesArr.length;
    }
  }, [userData?.favoritesArr]);

  useEffect(() => {
    const currentFavoritesCount = userData?.favoritesArr?.length || 0;
    const previousCount = previousFavoritesCountRef.current;

    if (previousCount !== null && currentFavoritesCount !== previousCount) {
      if (currentFavoritesCount > previousCount) {
        setSnackbarMessage(translate('AddedFavBenefit') || 'ההטבה נוספה למועדפים');
      } else {
        setSnackbarMessage(translate('RemovedFavBenefit') || 'ההטבה הוסרה מהמועדפים');
      }
      setShowSnackbar(true);
    }

    previousFavoritesCountRef.current = currentFavoritesCount;
  }, [userData?.favoritesArr, translate]);

  return (
    <Container testId={testID}>
      <Header testId={generateTestId(testID, 'header')}>
        <TextHot testID={generateTestId(testID, 'title')} size="xxxl" bold="bold" color="primary">
          {translate('my_favorites_personal_info_title')}
        </TextHot>
      </Header>

      {isLoading && (
        <LoaderContainer testId={generateTestId(testID, 'loader')}>
          <Loader />
        </LoaderContainer>
      )}

      {!isLoading && favorites && (
        <GridContainer testId={generateTestId(testID, 'grid')}>
          {favorites.map((item) => (
            <Benefit key={item.id} data={item} />
          ))}
        </GridContainer>
      )}

      {!isLoading && favorites && favorites.length === 0 && (
        <EmptyDataHot
          title={translate('no_favorites_text')}
          image={noFavoritesIcon}
          imageSize={80}
          testID={generateTestId(testID, 'empty-data')}
        />
      )}

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="success"
          sx={{ fontSize: '16px', fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FavoritesSection;
